import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import { Button, DateInput, Select } from '../../components/ui';
import { SearchIcon, PrinterIcon } from '../../components/ui/Icons';
import { formatDate as formatDateGlobal } from '../../config/appConfig';
import { fetcher } from '../../services/fetcher';

const todayIso = () => new Date().toISOString().slice(0, 10);

function mondayOfWeek(iso: string) {
  const d = new Date(`${iso}T00:00:00Z`);
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d.toISOString().slice(0, 10);
}

function addDaysIso(iso: string, days: number) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const WEEKDAY_SHORT = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

type DayCell = {
  workDate: string;
  workHours: number;
  restHours: number;
  compliant: boolean;
  violationCodes: string[];
};

type CrewRow = {
  crewId: number;
  crewName: string;
  rankName?: string | null;
  totalWork: number;
  totalRest: number;
  anyNc: boolean;
  days: DayCell[];
};

type WeeklyResponse = {
  vesselName?: string | null;
  weekStart: string;
  weekEnd: string;
  crew: CrewRow[];
};

function fmtHours(n: number): string {
  if (!Number.isFinite(n)) return '—';
  return n % 1 === 0 ? String(n) : n.toFixed(1);
}

const WeeklyReportPage: React.FC<{ lockedVesselId?: number }> = ({ lockedVesselId }) => {
  const { colors } = useTheme();
  const [params] = useSearchParams();
  const { data: lookups } = useSWR<{ vessels: Array<{ id: number; vesselName: string }> }>(
    '/crew/lookups',
    fetcher,
    { revalidateOnFocus: false },
  );

  const initialVessel =
    lockedVesselId != null
      ? String(lockedVesselId)
      : params.get('vesselId')?.trim() || '';
  const initialWeek = params.get('weekStart')?.trim()
    ? mondayOfWeek(params.get('weekStart')!.trim())
    : mondayOfWeek(todayIso());

  const [vesselId, setVesselId] = useState(initialVessel);
  const [weekStart, setWeekStart] = useState(initialWeek);
  const [applied, setApplied] = useState<{ vesselId: string; weekStart: string } | null>(
    initialVessel ? { vesselId: initialVessel, weekStart: initialWeek } : null,
  );
  const [selectedCrewId, setSelectedCrewId] = useState<number | null>(null);

  const vesselOptions = useMemo(
    () => (lookups?.vessels ?? []).map((v) => ({ value: String(v.id), label: v.vesselName })),
    [lookups?.vessels],
  );

  const key =
    applied && `/wrh/reports/weekly?vesselId=${applied.vesselId}&weekStart=${applied.weekStart}`;
  const { data, isLoading } = useSWR<WeeklyResponse>(key, fetcher, { revalidateOnFocus: false });

  const weekDates = useMemo(() => {
    const start = applied?.weekStart ?? weekStart;
    return Array.from({ length: 7 }, (_, i) => addDaysIso(start, i));
  }, [applied?.weekStart, weekStart]);

  const crew = data?.crew ?? [];
  const selectedCrew = useMemo(() => {
    if (!crew.length) return null;
    return crew.find((c) => c.crewId === selectedCrewId) ?? crew[0];
  }, [crew, selectedCrewId]);

  const ncCrew = crew.filter((c) => c.anyNc).length;
  const okCrew = crew.length - ncCrew;
  const totalWork = crew.reduce((s, c) => s + (c.totalWork || 0), 0);
  const totalRest = crew.reduce((s, c) => s + (c.totalRest || 0), 0);

  const dayByDate = (row: CrewRow) => {
    const map = new Map(row.days.map((d) => [d.workDate.slice(0, 10), d]));
    return map;
  };

  const shiftWeek = (delta: number) => {
    const next = mondayOfWeek(addDaysIso(weekStart, delta * 7));
    setWeekStart(next);
    if (applied) {
      setApplied({ ...applied, weekStart: next });
      setSelectedCrewId(null);
    }
  };

  const run = () => {
    const vid = lockedVesselId ? String(lockedVesselId) : vesselId;
    if (!vid) return;
    const ws = mondayOfWeek(weekStart);
    setWeekStart(ws);
    setApplied({ vesselId: vid, weekStart: ws });
    setSelectedCrewId(null);
  };

  return (
    <div className={cn('flex h-full min-h-0 flex-col gap-2', colors.background)}>
      <div
        className={cn(
          'flex shrink-0 flex-wrap items-center gap-2 rounded-xl border px-3 py-2',
          colors.border,
          colors.surface,
        )}
      >
        {!lockedVesselId && (
          <div className="w-[11rem]">
            <Select
              value={vesselId}
              onChange={(e) => setVesselId(e.target.value)}
              options={vesselOptions}
              placeholder="Vessel"
              showClearButton={false}
            />
          </div>
        )}
        <Button type="button" size="sm" variant="secondary" onClick={() => shiftWeek(-1)} title="Previous week">
          ←
        </Button>
        <div className="w-[9.5rem]">
          <DateInput
            value={weekStart}
            onChange={(e) => setWeekStart(mondayOfWeek(e.target.value || todayIso()))}
            aria-label="Week start"
          />
        </div>
        <Button type="button" size="sm" variant="secondary" onClick={() => shiftWeek(1)} title="Next week">
          →
        </Button>
        <Button type="button" size="sm" onClick={run}>
          <SearchIcon className="h-4 w-4" />
          Search
        </Button>

        {data && (
          <>
            <div className={cn('mx-1 hidden h-6 w-px sm:block', 'bg-black/10 dark:bg-white/10')} />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <span className={cn('truncate text-sm font-semibold', colors.text)}>
                  {data.vesselName || 'Weekly report'}
                </span>
                <span className={cn('truncate text-xs', colors.textMuted)}>
                  {formatDateGlobal(data.weekStart)} – {formatDateGlobal(data.weekEnd)} · {crew.length}{' '}
                  crew
                </span>
              </div>
            </div>
          </>
        )}

        {data && applied ? (
          <Link
            to={`/wrh/reports/report?vesselId=${encodeURIComponent(applied.vesselId)}&weekStart=${encodeURIComponent(applied.weekStart)}`}
            className="ml-auto shrink-0"
          >
            <Button type="button" size="sm" variant="secondary">
              <PrinterIcon className="h-4 w-4" />
              Print
            </Button>
          </Link>
        ) : null}
      </div>

      {!applied && !isLoading && (
        <div
          className={cn(
            'flex flex-1 items-center justify-center rounded-xl border border-dashed text-sm',
            colors.border,
            colors.textMuted,
          )}
        >
          Choose vessel and week, then Search.
        </div>
      )}

      {isLoading && (
        <p className={cn('shrink-0 text-sm', colors.textMuted)}>Loading weekly report…</p>
      )}

      {data && (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
          <div
            className={cn(
              'flex shrink-0 flex-wrap items-center gap-2 rounded-xl border px-3 py-2',
              colors.border,
              colors.surface,
            )}
          >
            <StatChip label="Crew" value={String(crew.length)} />
            <StatChip
              label="OK"
              value={String(okCrew)}
              tone="ok"
            />
            <StatChip
              label="NC"
              value={String(ncCrew)}
              tone={ncCrew > 0 ? 'bad' : 'muted'}
            />
            <StatChip label="Work" value={`${fmtHours(totalWork)}h`} />
            <StatChip label="Rest" value={`${fmtHours(totalRest)}h`} />
            <span className={cn('ml-auto hidden text-[10px] sm:inline', colors.textMuted)}>
              Day cells show work hours · red = non-conformity
            </span>
          </div>

          <div className="flex min-h-0 flex-1 gap-2">
            <div
              className={cn(
                'flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-xl border',
                colors.border,
                colors.surface,
              )}
            >
              {crew.length === 0 ? (
                <div className={cn('flex flex-1 items-center justify-center text-sm', colors.textMuted)}>
                  No WRH days in this week.
                </div>
              ) : (
                <div className="min-h-0 flex-1 overflow-auto">
                  <table className="w-full min-w-[640px] text-sm">
                    <thead>
                      <tr
                        className={cn(
                          'sticky top-0 z-10 border-b text-[10px] font-semibold uppercase tracking-wide',
                          colors.border,
                          colors.textSecondary,
                          'bg-white/95 backdrop-blur-sm dark:bg-zinc-950/95',
                        )}
                      >
                        <th className="px-3 py-2 text-left">Crew</th>
                        <th className="hidden px-2 py-2 text-left sm:table-cell">Rank</th>
                        {weekDates.map((date, i) => (
                          <th key={date} className="px-1.5 py-2 text-center">
                            <div>{WEEKDAY_SHORT[i]}</div>
                            <div className={cn('font-medium normal-case tracking-normal', colors.textMuted)}>
                              {date.slice(8)}
                            </div>
                          </th>
                        ))}
                        <th className="px-2 py-2 text-right">Work</th>
                        <th className="px-2 py-2 text-right">Rest</th>
                        <th className="px-2 py-2 text-center">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crew.map((c, idx) => {
                        const byDate = dayByDate(c);
                        const active = selectedCrew?.crewId === c.crewId;
                        return (
                          <tr
                            key={c.crewId}
                            className={cn(
                              'border-b border-black/[0.04] dark:border-white/[0.04]',
                              idx % 2 === 1 && 'bg-black/[0.015] dark:bg-white/[0.02]',
                              active && 'bg-sky-500/[0.06]',
                              'cursor-pointer',
                            )}
                            onClick={() => setSelectedCrewId(c.crewId)}
                          >
                            <td className={cn('px-3 py-1.5 font-medium', colors.text)}>{c.crewName}</td>
                            <td className={cn('hidden px-2 py-1.5 sm:table-cell', colors.textMuted)}>
                              {c.rankName || '—'}
                            </td>
                            {weekDates.map((date) => {
                              const d = byDate.get(date);
                              if (!d) {
                                return (
                                  <td key={date} className="px-1.5 py-1.5 text-center">
                                    <span className={cn('text-[10px]', colors.textMuted)}>—</span>
                                  </td>
                                );
                              }
                              const nc = !d.compliant;
                              return (
                                <td key={date} className="px-1.5 py-1.5 text-center">
                                  <span
                                    title={
                                      nc
                                        ? `NC: ${(d.violationCodes ?? []).join(', ') || 'issue'}\nWork ${fmtHours(d.workHours)}h · Rest ${fmtHours(d.restHours)}h`
                                        : `Work ${fmtHours(d.workHours)}h · Rest ${fmtHours(d.restHours)}h`
                                    }
                                    className={cn(
                                      'inline-flex min-w-[2.25rem] items-center justify-center rounded-md px-1 py-0.5 text-[11px] font-semibold tabular-nums',
                                      nc
                                        ? 'bg-rose-500/15 text-rose-700 dark:text-rose-300'
                                        : 'bg-emerald-500/10 text-emerald-800 dark:text-emerald-300',
                                    )}
                                  >
                                    {fmtHours(d.workHours)}
                                  </span>
                                </td>
                              );
                            })}
                            <td className={cn('px-2 py-1.5 text-right tabular-nums font-medium', colors.text)}>
                              {fmtHours(c.totalWork)}
                            </td>
                            <td className={cn('px-2 py-1.5 text-right tabular-nums', colors.textMuted)}>
                              {fmtHours(c.totalRest)}
                            </td>
                            <td className="px-2 py-1.5 text-center">
                              {c.anyNc ? (
                                <span className="inline-flex rounded-md bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-bold text-rose-700 dark:text-rose-300">
                                  NC
                                </span>
                              ) : (
                                <span className="inline-flex rounded-md bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold text-emerald-700 dark:text-emerald-300">
                                  OK
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {selectedCrew && selectedCrew.days.length > 0 && (
                <div
                  className={cn(
                    'shrink-0 border-t px-3 py-2.5',
                    colors.border,
                    'bg-zinc-50/80 dark:bg-zinc-900/80',
                  )}
                >
                  <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2">
                    <div>
                      <span className={cn('text-sm font-semibold', colors.text)}>
                        {selectedCrew.crewName}
                      </span>
                      <span className={cn('ml-2 text-xs', colors.textMuted)}>
                        {selectedCrew.rankName || '—'} · week work {fmtHours(selectedCrew.totalWork)}h ·
                        rest {fmtHours(selectedCrew.totalRest)}h
                      </span>
                    </div>
                    {selectedCrew.anyNc ? (
                      <span className="text-[11px] font-medium text-rose-600 dark:text-rose-400">
                        Has non-conformities
                      </span>
                    ) : (
                      <span className="text-[11px] font-medium text-emerald-600 dark:text-emerald-400">
                        Compliant week
                      </span>
                    )}
                  </div>
                  <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-4">
                    {weekDates.map((date, i) => {
                      const d = dayByDate(selectedCrew).get(date);
                      return (
                        <div
                          key={date}
                          className={cn(
                            'rounded-lg border px-2.5 py-1.5',
                            colors.border,
                            colors.surface,
                            d && !d.compliant && 'border-rose-500/30 bg-rose-500/[0.04]',
                          )}
                        >
                          <div className="flex items-baseline justify-between gap-2">
                            <span className={cn('text-[11px] font-semibold', colors.text)}>
                              {WEEKDAY_SHORT[i]} {date.slice(8)}
                            </span>
                            {d ? (
                              d.compliant ? (
                                <span className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
                                  OK
                                </span>
                              ) : (
                                <span className="text-[10px] font-medium text-rose-600 dark:text-rose-400">
                                  NC
                                </span>
                              )
                            ) : (
                              <span className={cn('text-[10px]', colors.textMuted)}>No day</span>
                            )}
                          </div>
                          {d && (
                            <>
                              <div className={cn('mt-0.5 text-[11px] tabular-nums', colors.textMuted)}>
                                Work {fmtHours(d.workHours)}h · Rest {fmtHours(d.restHours)}h
                              </div>
                              {!d.compliant && (d.violationCodes?.length ?? 0) > 0 && (
                                <div className="mt-1 flex flex-wrap gap-1">
                                  {d.violationCodes.map((code) => (
                                    <span
                                      key={code}
                                      className="rounded border border-rose-500/25 bg-rose-500/10 px-1 py-0.5 text-[10px] font-medium text-rose-700 dark:text-rose-300"
                                    >
                                      {code.replace(/_/g, ' ')}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatChip: React.FC<{
  label: string;
  value: string;
  tone?: 'ok' | 'bad' | 'muted';
}> = ({ label, value, tone }) => {
  const { colors } = useTheme();
  return (
    <span
      className={cn(
        'inline-flex items-baseline gap-1.5 rounded-lg border px-2.5 py-1',
        colors.border,
        tone === 'ok' && 'border-emerald-500/25 bg-emerald-500/10',
        tone === 'bad' && 'border-rose-500/25 bg-rose-500/10',
      )}
    >
      <span className={cn('text-[10px] uppercase tracking-wide', colors.textMuted)}>{label}</span>
      <span
        className={cn(
          'text-sm font-semibold tabular-nums',
          tone === 'ok' && 'text-emerald-700 dark:text-emerald-300',
          tone === 'bad' && 'text-rose-700 dark:text-rose-300',
          !tone && colors.text,
        )}
      >
        {value}
      </span>
    </span>
  );
};

export default WeeklyReportPage;
